import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Package, Check, X, Loader2, Sparkles, Save, ShieldCheck, Undo2, Eye, AlertTriangle, Plus, Trash2, ArrowLeft,
} from 'lucide-react';

import FitterSends from './FitterSends';

const API = '/api';

/**
 * Fitter Marketing packs (backend: fitter_packs.py, fitter_packs_api.py).
 *
 * Randall, 21 Oct 2026: "emails and posts that have images etc... so they have professional emails and posts,
 * PDF's they can print on all products", and "when we create new products or campaigns we should have something
 * that connects with the Hub asking if we want to put this campaign into Fitters Marketing packs".
 *
 * A pack is one product's email, post captions and print words, with the library pictures that go with them. The
 * website's Marketing page shows a pack only once it is approved here, and only while it reads exactly as approved:
 * change a word or a picture and it comes off the website until it is approved again.
 */

const TOKENS_HELP = 'The website fills these in for each fitter: {{name}} {{title}} {{city}} {{phone}} {{contact}} {{book_url}}';

function toForm(p) {
  const e = p.email || {};
  const f = p.flyer || {};
  const lines = [...(f.lines || [])];
  while (lines.length < 3) lines.push('');
  return {
    label: p.label || '',
    product: p.product || '',
    subject: e.subject || '',
    preheader: e.preheader || '',
    headline: e.headline || '',
    body: (e.body || []).join('\n\n'),
    button: e.button || 'Book your fitting',
    captions: (p.captions && p.captions.length ? [...p.captions] : ['']),
    flyerHeadline: f.headline || '',
    lines,
    counter: f.counter || '',
    picture_ids: [...(p.picture_ids || [])],
  };
}

function fromForm(f) {
  const clean = (xs) => xs.map((x) => (x || '').trim()).filter(Boolean);
  return {
    label: f.label.trim(),
    product: f.product.trim(),
    email: {
      subject: f.subject.trim(),
      preheader: f.preheader.trim(),
      headline: f.headline.trim(),
      body: clean(f.body.split(/\n\s*\n/)),
      button: f.button.trim(),
    },
    captions: clean(f.captions),
    flyer: { headline: f.flyerHeadline.trim(), lines: clean(f.lines), counter: f.counter.trim() },
    picture_ids: f.picture_ids,
  };
}

function Chip({ pack }) {
  if (pack.status === 'approved') {
    return <span className="text-[10px] font-sans uppercase tracking-wider bg-hg-green/10 text-hg-green border border-hg-green/30 rounded-sm px-1.5 py-0.5">On the website</span>;
  }
  if (pack.changed_since_approval) {
    return <span className="text-[10px] font-sans uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-300 rounded-sm px-1.5 py-0.5">Changed since approval</span>;
  }
  return <span className="text-[10px] font-sans uppercase tracking-wider bg-hg-surface-hover text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5">Draft</span>;
}

function Field({ label, children, hint }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-sans uppercase tracking-wider text-hg-text-dim">{label}</span>
      {children}
      {hint && <span className="block text-[11px] font-sans text-hg-text-dim">{hint}</span>}
    </label>
  );
}

const inputCls = 'w-full bg-white border border-hg-border rounded-sm px-2.5 py-1.5 text-sm font-sans text-hg-text focus:outline-none focus:border-hg-green';

function DecideRow({ item, kind, products, onDone }) {
  const [product, setProduct] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const answer = async (decision) => {
    setBusy(decision); setErr('');
    try {
      await axios.post(`${API}/fitter-packs/decide`, { kind, key: item.key, decision, product: product || undefined });
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save the answer');
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap py-2 border-b border-hg-border/60 last:border-0" data-testid={`decide-${kind}-${item.key}`}>
      <span className="text-sm font-sans text-hg-text font-medium">{item.label || item.name}</span>
      <span className="text-[11px] font-sans text-hg-text-dim">{kind === 'campaign' ? 'campaign' : 'product'}</span>
      {kind === 'campaign' && (
        <select value={product} onChange={(e) => setProduct(e.target.value)} className="ml-2 bg-white border border-hg-border rounded-sm px-2 py-1 text-xs font-sans">
          <option value="">Which product?</option>
          {products.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          <option value="brand">Henry-Griffitts (no one product)</option>
        </select>
      )}
      <div className="ml-auto flex gap-2">
        <button onClick={() => answer('yes')} disabled={!!busy || (kind === 'campaign' && !product)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-white rounded-sm hover:opacity-90 disabled:opacity-40">
          {busy === 'yes' ? <Loader2 size={11} className="animate-spin" /> : <Check size={12} />} Yes, make a pack
        </button>
        <button onClick={() => answer('no')} disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:bg-hg-surface-hover disabled:opacity-40">
          {busy === 'no' ? <Loader2 size={11} className="animate-spin" /> : <X size={12} />} No
        </button>
      </div>
      {err && <div className="w-full text-[11px] font-sans text-red-700">{err}</div>}
    </div>
  );
}

function PictureChooser({ choices, selected, onChange }) {
  if (!choices.length) {
    return <div className="text-xs font-sans text-hg-text-dim italic">This product has no photos tagged for fitters in the library yet.</div>;
  }
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {choices.map((c) => {
        const n = selected.indexOf(c.id);
        return (
          <button type="button" key={c.id} onClick={() => toggle(c.id)} title={c.title}
            className={`relative text-left border rounded-sm overflow-hidden ${n >= 0 ? 'border-hg-green ring-2 ring-hg-green/40' : 'border-hg-border'}`}>
            <img src={c.thumb} alt={c.title} className="w-full aspect-[3/2] object-contain bg-white" loading="lazy" />
            <div className="px-1.5 py-1 text-[10px] font-sans text-hg-text-dim truncate">{c.title}</div>
            {n >= 0 && (
              <span className="absolute top-1 left-1 bg-hg-green text-white text-[10px] font-sans rounded-sm px-1.5 py-0.5">
                {n === 0 ? '1 · leads the email' : n + 1}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function PackEditor({ pack, choices, onChanged, onBack }) {
  const [current, setCurrent] = useState(pack);
  const [form, setForm] = useState(() => toForm(pack));
  const [problems, setProblems] = useState(pack.problems || []);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState('');
  const dirty = JSON.stringify(fromForm(form)) !== JSON.stringify(fromForm(toForm(current)));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const refresh = (view) => {
    setCurrent(view); setForm(toForm(view)); setProblems(view.problems || []); onChanged();
  };

  const run = async (what, fn) => {
    setBusy(what); setMsg('');
    try {
      await fn();
    } catch (e) {
      const d = e?.response?.data;
      if (d?.problems) setProblems(d.problems);
      setMsg(d?.detail || (d?.problems ? 'Not approved yet: see what it needs below.' : 'That did not work. Nothing was changed.'));
    } finally {
      setBusy('');
    }
  };

  const draft = () => run('draft', async () => {
    const res = await axios.post(`${API}/fitter-packs/${encodeURIComponent(current.id)}/draft`);
    setForm(toForm({ ...current, ...res.data.draft }));
    setProblems(res.data.problems || []);
    setMsg('The Voice Engine filled in the words. Nothing is saved yet: read it, change what you like, then Save.');
  });
  const save = () => run('save', async () => {
    const res = await axios.put(`${API}/fitter-packs/${encodeURIComponent(current.id)}`, fromForm(form));
    refresh(res.data);
    setMsg(res.data.status === 'approved' ? 'Saved. Nothing changed, so it is still on the website.' : 'Saved. Fitters see it once you approve it.');
  });
  const approve = () => run('approve', async () => {
    await axios.post(`${API}/fitter-packs/${encodeURIComponent(current.id)}/approve`);
    const res = await axios.get(`${API}/fitter-packs/${encodeURIComponent(current.id)}`);
    refresh(res.data);
    setMsg('Approved. Fitters see it on their Marketing page within about ten minutes.');
  });
  const withdraw = () => run('withdraw', async () => {
    await axios.post(`${API}/fitter-packs/${encodeURIComponent(current.id)}/withdraw`);
    const res = await axios.get(`${API}/fitter-packs/${encodeURIComponent(current.id)}`);
    refresh(res.data);
    setMsg('Taken off the website. Its words are kept.');
  });
  const showPreview = () => run('preview', async () => {
    const res = await axios.get(`${API}/fitter-packs/${encodeURIComponent(current.id)}/preview`, { responseType: 'text' });
    setPreview(res.data);
  });

  const captions = form.captions;
  const setCaption = (i, v) => setForm({ ...form, captions: captions.map((c, j) => (j === i ? v : c)) });
  const setLine = (i, v) => setForm({ ...form, lines: form.lines.map((l, j) => (j === i ? v : l)) });

  return (
    <div className="space-y-5" data-testid="pack-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-xs font-sans text-hg-text-dim hover:text-hg-text"><ArrowLeft size={13} /> All packs</button>
        <h2 className="text-lg font-display text-hg-text">{current.label || current.id}</h2>
        <Chip pack={current} />
        {current.source?.kind === 'campaign' && <span className="text-[11px] font-sans text-hg-text-dim">campaign pack · under {current.product}</span>}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={draft} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green/40 text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40">
          {busy === 'draft' ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={12} />} Draft the words
        </button>
        <button onClick={save} disabled={!!busy || !dirty} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-white rounded-sm hover:opacity-90 disabled:opacity-40">
          {busy === 'save' ? <Loader2 size={11} className="animate-spin" /> : <Save size={12} />} Save
        </button>
        <button onClick={showPreview} disabled={!!busy || dirty} title={dirty ? 'Save first' : ''} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:bg-hg-surface-hover disabled:opacity-40">
          {busy === 'preview' ? <Loader2 size={11} className="animate-spin" /> : <Eye size={12} />} Look at the email
        </button>
        {current.status !== 'approved' && (
          <button onClick={approve} disabled={!!busy || dirty || problems.length > 0} title={dirty ? 'Save first' : ''}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-text text-white rounded-sm hover:opacity-90 disabled:opacity-40">
            {busy === 'approve' ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={12} />} Approve for fitters
          </button>
        )}
        {current.status === 'approved' && (
          <button onClick={withdraw} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:bg-hg-surface-hover disabled:opacity-40">
            {busy === 'withdraw' ? <Loader2 size={11} className="animate-spin" /> : <Undo2 size={12} />} Take off the website
          </button>
        )}
      </div>
      {msg && <div className="text-xs font-sans text-hg-text bg-hg-surface-hover border border-hg-border rounded-sm px-3 py-2">{msg}</div>}
      {current.approval && current.status === 'approved' && (
        <div className="text-[11px] font-sans text-hg-text-dim">Approved by {current.approval.by} · {String(current.approval.at || '').slice(0, 16).replace('T', ' ')} UTC</div>
      )}
      {problems.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 space-y-1" data-testid="pack-problems">
          <div className="text-[11px] font-sans uppercase tracking-wider text-amber-800 inline-flex items-center gap-1"><AlertTriangle size={11} /> Before it can be approved</div>
          {problems.map((p, i) => <div key={i} className="text-xs font-sans text-amber-900">{p}</div>)}
        </div>
      )}

      <section className="space-y-2">
        <div className="text-sm font-sans font-medium text-hg-text">Pictures</div>
        <div className="text-[11px] font-sans text-hg-text-dim">Real photos tagged for fitters. The first leads the email and the flyer; tap again to take one out.</div>
        <PictureChooser choices={choices} selected={form.picture_ids} onChange={(ids) => setForm({ ...form, picture_ids: ids })} />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Pack name (the email's eyebrow)"><input className={inputCls} value={form.label} onChange={set('label')} /></Field>
        <Field label="Email subject"><input className={inputCls} value={form.subject} onChange={set('subject')} /></Field>
        <Field label="Preview line (shown after the subject in the inbox)"><input className={inputCls} value={form.preheader} onChange={set('preheader')} /></Field>
        <Field label="Headline"><input className={inputCls} value={form.headline} onChange={set('headline')} /></Field>
        <div className="md:col-span-2">
          <Field label="The email's words" hint={`A blank line starts a new paragraph. ${TOKENS_HELP}`}>
            <textarea className={`${inputCls} min-h-[140px]`} value={form.body} onChange={set('body')} />
          </Field>
        </div>
        <Field label="Button" hint="It opens the fitter's own Find a Fitter page."><input className={inputCls} value={form.button} onChange={set('button')} /></Field>
      </section>

      <section className="space-y-2">
        <div className="text-sm font-sans font-medium text-hg-text">Post captions</div>
        {captions.map((c, i) => (
          <div key={i} className="flex gap-2 items-start">
            <textarea className={`${inputCls} min-h-[84px]`} value={c} onChange={(e) => setCaption(i, e.target.value)} />
            {captions.length > 1 && (
              <button type="button" onClick={() => setForm({ ...form, captions: captions.filter((_, j) => j !== i) })} className="p-1.5 text-hg-text-dim hover:text-red-700" title="Remove"><Trash2 size={13} /></button>
            )}
          </div>
        ))}
        {captions.length < 3 && (
          <button type="button" onClick={() => setForm({ ...form, captions: [...captions, ''] })} className="inline-flex items-center gap-1 text-xs font-sans text-hg-green"><Plus size={12} /> Another caption</button>
        )}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Flyer headline"><input className={inputCls} value={form.flyerHeadline} onChange={set('flyerHeadline')} /></Field>
        <Field label="Counter card line"><input className={inputCls} value={form.counter} onChange={set('counter')} /></Field>
        {form.lines.map((l, i) => (
          <Field key={i} label={`Flyer line ${i + 1}`}><input className={inputCls} value={l} onChange={(e) => setLine(i, e.target.value)} /></Field>
        ))}
      </section>

      {preview && (
        <section className="space-y-2">
          <div className="text-sm font-sans font-medium text-hg-text">The email, as a sample fitter's golfers would get it</div>
          <iframe title="Email preview" srcDoc={preview} sandbox="" className="w-full h-[900px] border border-hg-border rounded-sm bg-white" />
        </section>
      )}
    </div>
  );
}

export default function FitterPacks() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/fitter-packs`)
      .then((res) => { setData(res.data); setError(''); })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load the packs'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="p-6 text-sm font-sans text-red-700">{error}</div>;
  if (!data) return <div className="p-6"><Loader2 className="animate-spin text-hg-text-dim" size={18} /></div>;

  const pack = open && data.packs.find((p) => p.id === open);
  if (pack) {
    return (
      <div className="p-6 max-w-5xl">
        <PackEditor key={pack.id} pack={pack} choices={data.pictures[pack.product] || []} onChanged={load} onBack={() => setOpen(null)} />
      </div>
    );
  }

  const toDecide = [...data.decide.products.map((p) => ['product', p]), ...data.decide.campaigns.map((c) => ['campaign', c])];
  const noes = data.decided.filter((d) => d.decision === 'no');
  return (
    <div className="p-6 max-w-5xl space-y-6" data-testid="fitter-packs">
      <div>
        <h1 className="text-xl font-display text-hg-text inline-flex items-center gap-2"><Package size={18} className="text-hg-green" /> Fitter Marketing packs</h1>
        <p className="text-sm font-sans text-hg-text-dim mt-1 max-w-3xl">
          Each product's email, post captions and flyer words for the fitters' Marketing page, with the pictures that go with them.
          The website fills in each fitter's name, rung, town, phone and booking link. Fitters see a pack once you approve it here,
          and only while it reads exactly as you approved it.
        </p>
      </div>

      <FitterSends />

      {toDecide.length > 0 && (
        <section className="bg-white border border-hg-border rounded-sm px-4 py-3">
          <div className="text-sm font-sans font-medium text-hg-text mb-1">Put these in the fitters' Marketing packs?</div>
          {toDecide.map(([kind, item]) => (
            <DecideRow key={`${kind}-${item.key}`} kind={kind} item={item} products={data.products} onDone={load} />
          ))}
        </section>
      )}

      <section className="space-y-2">
        <div className="text-sm font-sans font-medium text-hg-text">Packs</div>
        {data.packs.length === 0 && <div className="text-xs font-sans text-hg-text-dim italic">No packs yet. Say yes to a product above to start one.</div>}
        {data.packs.map((p) => (
          <button key={p.id} onClick={() => setOpen(p.id)} data-testid={`pack-${p.id}`}
            className="w-full text-left bg-white border border-hg-border rounded-sm px-4 py-3 hover:bg-hg-surface-hover flex items-center gap-3 flex-wrap">
            <span className="text-sm font-sans text-hg-text font-medium">{p.label || p.id}</span>
            <Chip pack={p} />
            {p.source?.kind === 'campaign' && <span className="text-[11px] font-sans text-hg-text-dim">campaign</span>}
            <span className="ml-auto text-[11px] font-sans text-hg-text-dim">
              {p.problems.length ? `${p.problems.length} thing${p.problems.length === 1 ? '' : 's'} to finish` : (p.status === 'approved' ? 'ready' : 'ready to approve')}
            </span>
          </button>
        ))}
      </section>

      {(noes.length > 0 || data.not_for_fitters.length > 0) && (
        <section className="space-y-1">
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-text-dim">Not in the packs</div>
          {data.not_for_fitters.map((n) => (
            <div key={n.key} className="text-xs font-sans text-hg-text-dim">{n.label}: {n.why}</div>
          ))}
          {noes.map((d) => (
            <div key={`${d.kind}-${d.key}`} className="text-xs font-sans text-hg-text-dim flex items-center gap-2">
              <span>{d.kind === 'product' ? (data.products.find((p) => p.key === d.key)?.label || d.key) : d.key}: you said no{d.by ? ` (${d.by}, ${String(d.at || '').slice(0, 10)})` : ''}.</span>
              {d.kind === 'product' && (
                <button className="text-hg-green underline" onClick={() => axios.post(`${API}/fitter-packs/decide`, { kind: 'product', key: d.key, decision: 'yes' }).then(load)}>Make a pack after all</button>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
